import apiClient from '@/api/client';
import { getActiveHospitalId, getActiveClinicId } from '@/constants';

// Initial mock templates for immediate UI capability and testing
const DEFAULT_TEMPLATES = [
  {
    id: 'tmpl-001',
    name: 'Common Cold & Upper Respiratory Infection',
    category: 'General OPD',
    description: 'Standard 5-day symptomatic treatment for acute viral rhinitis / URI',
    items: [
      {
        id: 'ti-1',
        medicineName: 'Paracetamol 650 mg (Dolo 650)',
        amount: '1 tablet',
        quantity: 10,
        frequency: 'Thrice daily (TDS)',
        duration: '3 days',
        notes: 'Take after food for fever or severe body pain',
        displayOrder: 1,
      },
      {
        id: 'ti-2',
        medicineName: 'Cetirizine 10 mg',
        amount: '1 tablet',
        quantity: 5,
        frequency: 'Once daily (Night)',
        duration: '5 days',
        notes: 'At bedtime, may cause mild drowsiness',
        displayOrder: 2,
      },
      {
        id: 'ti-3',
        medicineName: 'Azithromycin 500 mg',
        amount: '1 tablet',
        quantity: 3,
        frequency: 'Once daily (Morning)',
        duration: '3 days',
        notes: 'Take 1 hour before or 2 hours after food',
        displayOrder: 3,
      },
    ],
  },
  {
    id: 'tmpl-002',
    name: 'Acute Gastritis & Acid Peptic Disorder',
    category: 'Gastroenterology',
    description: 'Anti-secretory and antacid management protocol',
    items: [
      {
        id: 'ti-4',
        medicineName: 'Pantoprazole 40 mg (Pan 40)',
        amount: '1 tablet',
        quantity: 14,
        frequency: 'Once daily (Morning)',
        duration: '14 days',
        notes: 'Empty stomach 30 minutes before breakfast',
        displayOrder: 1,
      },
      {
        id: 'ti-5',
        medicineName: 'Domperidone 10 mg',
        amount: '1 tablet',
        quantity: 10,
        frequency: 'Twice daily (BD)',
        duration: '5 days',
        notes: 'Before meals for nausea / bloating',
        displayOrder: 2,
      },
    ],
  },
  {
    id: 'tmpl-003',
    name: 'Hypertension Maintenance Protocol',
    category: 'Cardiology',
    description: 'First line anti-hypertensive maintenance therapy',
    items: [
      {
        id: 'ti-6',
        medicineName: 'Telmisartan 40 mg (Telma 40)',
        amount: '1 tablet',
        quantity: 30,
        frequency: 'Once daily (Morning)',
        duration: '30 days',
        notes: 'Take with or without food at fixed morning hour. Monitor BP weekly.',
        displayOrder: 1,
      },
    ],
  },
];

const STORAGE_KEY = 'pulseflow_prescription_templates';

const getStoredTemplates = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_TEMPLATES));
      return DEFAULT_TEMPLATES;
    }
    return JSON.parse(raw);
  } catch (e) {
    return DEFAULT_TEMPLATES;
  }
};

const saveStoredTemplates = (templates) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (e) {
    console.error('Failed to save templates in storage', e);
  }
};

export const prescriptionTemplateService = {
  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/prescription-templates
   */
  getAll: async (params = {}, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(params.clinicId);

    try {
      const res = await apiClient.get(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/prescription-templates`,
        { params }
      );
      if (res && Array.isArray(res.data || res)) {
        return { data: res.data || res };
      }
    } catch (err) {
      // Graceful fallback to client-managed templates when BE endpoint is pending
    }

    let templates = getStoredTemplates();
    if (params.search) {
      const q = params.search.toLowerCase();
      templates = templates.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.category && t.category.toLowerCase().includes(q)) ||
          t.items.some((i) => i.medicineName.toLowerCase().includes(q))
      );
    }
    return { data: templates };
  },

  /**
   * GET /pms/hospital/{hospitalId}/clinic/{clinicId}/prescription-templates/{id}
   */
  getById: async (id, clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);

    try {
      const res = await apiClient.get(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/prescription-templates/${id}`
      );
      return res.data || res;
    } catch (err) {
      const templates = getStoredTemplates();
      return templates.find((t) => t.id === id) || null;
    }
  },

  /**
   * POST /pms/hospital/{hospitalId}/clinic/{clinicId}/prescription-templates
   */
  create: async (data, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(data.clinicId);

    const newTemplate = {
      id: `tmpl-${Date.now()}`,
      name: data.name,
      category: data.category || 'General',
      description: data.description || '',
      doctorId: data.doctorId || null,
      isActive: true,
      items: (data.items || []).map((item, idx) => ({
        id: `ti-${Date.now()}-${idx}`,
        medicineName: item.medicineName || item.medication,
        amount: item.amount || item.dosage || '1 tablet',
        quantity: Number(item.quantity) || 10,
        frequency: item.frequency || 'Twice daily (BD)',
        duration: item.duration || '5 days',
        notes: item.notes || item.instructions || '',
        displayOrder: idx + 1,
      })),
      createdAt: new Date().toISOString(),
    };

    try {
      const res = await apiClient.post(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/prescription-templates`,
        newTemplate
      );
      return res.data || res;
    } catch (err) {
      const current = getStoredTemplates();
      const updated = [newTemplate, ...current];
      saveStoredTemplates(updated);
      return newTemplate;
    }
  },

  /**
   * PUT /pms/hospital/{hospitalId}/clinic/{clinicId}/prescription-templates/{id}
   */
  update: async (id, data, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(data.clinicId);

    try {
      const res = await apiClient.put(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/prescription-templates/${id}`,
        data
      );
      return res.data || res;
    } catch (err) {
      const current = getStoredTemplates();
      const idx = current.findIndex((t) => t.id === id);
      if (idx !== -1) {
        current[idx] = { ...current[idx], ...data };
        saveStoredTemplates(current);
        return current[idx];
      }
      return null;
    }
  },

  /**
   * DELETE /pms/hospital/{hospitalId}/clinic/{clinicId}/prescription-templates/{id}
   */
  delete: async (id, clinicId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const activeClinicId = getActiveClinicId(clinicId);

    try {
      await apiClient.delete(
        `/hospital/${activeHospId}/clinic/${activeClinicId}/prescription-templates/${id}`
      );
      return true;
    } catch (err) {
      const current = getStoredTemplates();
      const updated = current.filter((t) => t.id !== id);
      saveStoredTemplates(updated);
      return true;
    }
  },
};

export default prescriptionTemplateService;

